// Hand-maintained mirror of models/src/khorium_models/thermal_material_library.py
// Sync verified by backend/tests/shared/test_thermal_material_library_sync.py.
// Keep values aligned; frontend additions must land before their backend rows.

export interface ThermalProperties {
  conductivity: number;
  density: number;
  specific_heat: number;
}

export const THERMAL_MATERIALS: Record<string, ThermalProperties> = {
  aluminum_6061: { conductivity: 167.0, density: 2700.0, specific_heat: 896.0 },
  aluminum_7075: { conductivity: 130.0, density: 2810.0, specific_heat: 960.0 },
  steel_304: { conductivity: 16.2, density: 8000.0, specific_heat: 500.0 },
  carbon_steel_1045: {
    conductivity: 49.8,
    density: 7850.0,
    specific_heat: 486.0,
  },
  copper_c110: { conductivity: 388.0, density: 8940.0, specific_heat: 385.0 },
  titanium_ti6al4v: {
    conductivity: 6.7,
    density: 4430.0,
    specific_heat: 526.0,
  },
  inconel_718: { conductivity: 11.4, density: 8190.0, specific_heat: 435.0 },
  brass_c360: { conductivity: 115.0, density: 8500.0, specific_heat: 380.0 },
  aluminum_nitride: {
    conductivity: 285.0,
    density: 3260.0,
    specific_heat: 740.0,
  },
  diamond_cvd: { conductivity: 1500.0, density: 3515.0, specific_heat: 509.0 },
  // Dry air at 300 K, 1 atm: Incropera, Fundamentals of Heat and Mass Transfer,
  // Table A.4. SI units: W/(m·K), kg/m³, J/(kg·K).
  still_air: { conductivity: 0.0263, density: 1.1614, specific_heat: 1007 },
  // Pure Si at 300 K. k falls steeply with T — backend ships a densified
  // Incropera Table A.1 curve. Density is the crystal value; cp ≈ 700 J/(kg·K)
  // covers bake / PEB ranges (ENG-3069).
  silicon: { conductivity: 148.0, density: 2329.0, specific_heat: 700 },
  // Fused silica / thermal oxide at room temperature (ENG-3069).
  silicon_dioxide: {
    conductivity: 1.4,
    density: 2200.0,
    specific_heat: 730,
  },
  // 96% alumina ceramic, room-temperature order of magnitude (ENG-3085).
  alumina: { conductivity: 30.0, density: 3900.0, specific_heat: 880 },
  // Silicone rubber / PDMS-class elastomer (ENG-3085).
  silicone: { conductivity: 0.2, density: 1100.0, specific_heat: 1460 },
  // Cast / extruded acrylic (PMMA) — common electrostatic dielectric (ENG-3085).
  acrylic: { conductivity: 0.19, density: 1180.0, specific_heat: 1470 },
  // Aluminum 5052-H32 sheet alloy (ENG-3085).
  aluminum_5052: {
    conductivity: 138.0,
    density: 2680.0,
    specific_heat: 880,
  },
  // Pure tungsten at ~25 °C (ENG-3085).
  tungsten: { conductivity: 174.0, density: 19300.0, specific_heat: 132 },
};

export type ThermalMaterialName = keyof typeof THERMAL_MATERIALS;

// English-canonical labels for the CHT solid picker, alongside the mirror the
// picker iterates — the fluid and structural mirrors keep theirs the same way.
// It lived in ChtSolverSections.tsx until ENG-1920, where a material added to
// the dict above got no label and the picker rendered the raw key as its muted
// suffix ("Diamond (CVD) diamond_cvd"). Keys must cover THERMAL_MATERIALS.
export const THERMAL_MATERIAL_LABELS: Record<string, string> = {
  aluminum_6061: 'Aluminum 6061',
  aluminum_7075: 'Aluminum 7075',
  steel_304: 'Steel 304',
  carbon_steel_1045: 'Carbon Steel 1045',
  copper_c110: 'Copper C110',
  titanium_ti6al4v: 'Titanium Ti-6Al-4V',
  inconel_718: 'Inconel 718',
  brass_c360: 'Brass C360',
  aluminum_nitride: 'Aluminum Nitride',
  diamond_cvd: 'Diamond (CVD)',
  still_air: 'Still air',
  silicon: 'Silicon',
  silicon_dioxide: 'Silicon dioxide',
  alumina: 'Alumina',
  silicone: 'Silicone',
  acrylic: 'Acrylic',
  aluminum_5052: 'Aluminum 5052',
  tungsten: 'Tungsten',
};
